/**
 * Bildbereiche für Belege unkenntlich machen — ohne DOM, damit es sich testen lässt.
 *
 * Verpixeln allein ist nicht sicher: Feine Blöcke lassen sich bei Text in bekannter Schrift
 * zurückrechnen, indem man gerenderte Kandidaten auf dasselbe Raster verpixelt und vergleicht.
 * Deshalb hier grobe Blöcke — Kante mindestens die halbe kürzere Seite des Bereichs — und je
 * Block ein zufälliger Helligkeitsversatz, sodass der Blockwert kein exakter Mittelwert mehr
 * ist, gegen den sich abgleichen ließe. „Schwärzen" deckt vollständig ab. Hochgeladen wird in
 * beiden Fällen nur das Ergebnis; das Original verlässt den Browser nicht.
 */

export type RedactionMode = 'pixelate' | 'blackout';

export interface RedactionRegion {
  x: number;
  y: number;
  /** Negativ, wenn nach links/oben gezogen wurde */
  width: number;
  height: number;
  mode: RedactionMode;
}

/** Kleinste Blockkante in Bildpixeln */
export const MIN_BLOCK_PX = 16;
/** Größter Helligkeitsversatz je Block (von 255) */
export const BLOCK_NOISE = 32;

/** Blockkante für einen Bereich: mindestens halb so lang wie seine kürzere Seite. */
export function blockSizeFor(region: { width: number; height: number }): number {
  return Math.max(MIN_BLOCK_PX, Math.round(Math.min(Math.abs(region.width), Math.abs(region.height)) / 2));
}

/** Auf das Bild zuschneiden und auf ganze Pixel bringen; null, wenn nichts übrig bleibt. */
export function clampRegion(region: RedactionRegion, imageWidth: number, imageHeight: number): RedactionRegion | null {
  const x0 = Math.max(0, Math.floor(Math.min(region.x, region.x + region.width)));
  const y0 = Math.max(0, Math.floor(Math.min(region.y, region.y + region.height)));
  const x1 = Math.min(imageWidth, Math.ceil(Math.max(region.x, region.x + region.width)));
  const y1 = Math.min(imageHeight, Math.ceil(Math.max(region.y, region.y + region.height)));
  if (x1 - x0 < 1 || y1 - y0 < 1) return null;
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0, mode: region.mode };
}

/** Grenzen gleich großer Zellen — nie ein schmaler Reststreifen am Rand, der mehr verrät. */
function cellEdges(start: number, length: number, block: number): number[] {
  const cells = Math.max(1, Math.floor(length / block));
  return Array.from({ length: cells + 1 }, (_, k) => start + Math.round((k * length) / cells));
}

/**
 * Bereich im RGBA-Puffer (`ImageData.data`) unkenntlich machen — ändert `pixels` direkt.
 * `random` liefert Werte in [0, 1) und ist nur für Tests austauschbar.
 */
export function redactPixels(
  pixels: Uint8ClampedArray,
  imageWidth: number,
  imageHeight: number,
  input: RedactionRegion,
  random: () => number = Math.random
): void {
  const region = clampRegion(input, imageWidth, imageHeight);
  if (!region) return;

  const fill = (xStart: number, yStart: number, xEnd: number, yEnd: number, r: number, g: number, b: number) => {
    for (let y = yStart; y < yEnd; y++) {
      for (let x = xStart; x < xEnd; x++) {
        const i = (y * imageWidth + x) * 4;
        pixels[i] = r;
        pixels[i + 1] = g;
        pixels[i + 2] = b;
        pixels[i + 3] = 255;
      }
    }
  };

  if (region.mode === 'blackout') {
    fill(region.x, region.y, region.x + region.width, region.y + region.height, 0, 0, 0);
    return;
  }

  const block = blockSizeFor(region);
  const xs = cellEdges(region.x, region.width, block);
  const ys = cellEdges(region.y, region.height, block);
  for (let row = 0; row < ys.length - 1; row++) {
    for (let col = 0; col < xs.length - 1; col++) {
      const [xStart, xEnd, yStart, yEnd] = [xs[col], xs[col + 1], ys[row], ys[row + 1]];
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let y = yStart; y < yEnd; y++) {
        for (let x = xStart; x < xEnd; x++) {
          const i = (y * imageWidth + x) * 4;
          r += pixels[i];
          g += pixels[i + 1];
          b += pixels[i + 2];
          n++;
        }
      }
      const shift = Math.round((random() * 2 - 1) * BLOCK_NOISE);
      // Uint8ClampedArray rundet und kappt auf 0–255 selbst.
      fill(xStart, yStart, xEnd, yEnd, r / n + shift, g / n + shift, b / n + shift);
    }
  }
}
